package dev.youndie.site.pages.blog

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initLeydenAotCacheForAPlainKotlinJvmServicePage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("blog/LeydenAotCacheForAPlainKotlinJvmService.md", FrontMatterElement.Builder { addScalar("title", "Leyden AOT cache for a plain Kotlin/JVM service: what it gives, what it costs, and the three things Spring does for you that you'll do yourself"); addScalar("searchTitle", "Project Leyden AOT cache on Ktor without Spring or Quarkus: numbers, the five commands by hand, the Gradle plugin, and what breaks"); addScalar("date", "2026-09-09"); addScalar("library", "zavarnik"); addScalar("description", "How to give a Ktor, http4k or any application-plugin service a Project Leyden AOT cache on JDK 25: what the cache is and is not, what it did to a real service's cold start and memory, the training, verification and packaging that Spring Boot and Quarkus hide, the same by hand in five steps, and through a Gradle plugin."); }))
}

@Page("/blog/leyden-aot-cache-for-a-plain-kotlin-jvm-service")
@Layout(".components.layouts.MarkdownLayout")
@Composable
fun LeydenAotCacheForAPlainKotlinJvmServicePage() {
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("This is the how-to I could not find when I needed it: a Ktor service on the plain ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("application")
        }
        org.jetbrains.compose.web.dom.Text(" plugin, JDK 25, and the question of whether Project Leyden's AOT cache is worth an afternoon. Spring Boot and Quarkus answer it with a flag. Everything else gets two JVM options and a list of ways the result stops working without telling anyone. The numbers below are from a reference service and from a stand, each with its methodology next to it; the commands are the ones the plugin at the end runs for you, written out so you know what it hides.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-the-cache-does-and-what-it-does-not") }) {
        org.jetbrains.compose.web.dom.Text("What the cache does, and what it does not")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A training run of your application under ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-XX:AOTCacheOutput=app.aot")
        }
        org.jetbrains.compose.web.dom.Text(" (JEP 514, JDK 25) records every class it loaded and linked, a set of heap objects — the module graph, class mirrors, the lambda proxies it resolved — and the method profiles the JIT gathered (JEP 515). At the next start under ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-XX:AOTCache=app.aot")
        }
        org.jetbrains.compose.web.dom.Text(" the JVM maps that file and skips the loading, parsing, verification and linking of those classes, and starts with the archived objects in place. That is the whole effect: the part of start-up that is class loading disappears, and the first request pays less because its classes are already linked.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("What the cache does not hold on JDK 25 and 26 is compiled code. AOT code compilation is still a draft JEP, so C1 and C2 compile the same methods after the start either way — on zavarnik's Ktor sample C2 compiled 1 171 methods cold and 1 172 with the cache, ten runs each. The profiles help the JIT choose what to compile first, not skip it. A service is not warm from the first request, and throughput after warm-up does not change. And the cache is tied to what it was trained on: the same jars, byte for byte and timestamp for timestamp, in the same order, on the same JDK build, with the same modules and agents. Change any of that and the JVM starts without the cache, prints three lines to stderr and goes on, exit code 0.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-it-gave-a-ktor-service") }) {
        org.jetbrains.compose.web.dom.Text("What it gave a Ktor service")
    }
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/konekt") {
            org.jetbrains.compose.web.dom.Text("konekt")
        }
        org.jetbrains.compose.web.dom.Text(" is my reference project: an eSIM operator's account backend, Ktor CIO with Exposed and Postgres, 128 jars on the classpath, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("eclipse-temurin:25-jre")
        }
        org.jetbrains.compose.web.dom.Text(", deployed with a limit of one core and 1 GiB. Two images that differ by one layer — the cache, 65 MiB, trained inside a container of the first image against the stand's database and verified under ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-XX:AOTMode=on")
        }
        org.jetbrains.compose.web.dom.Text(", 5 329 of 5 332 application classes from the cache. Ten restarts of each, in alternation, on the build box under the chart's limits; ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("docker start")
        }
        org.jetbrains.compose.web.dom.Text(" to the first ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("200")
        }
        org.jetbrains.compose.web.dom.Text(" on ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/health")
        }
        org.jetbrains.compose.web.dom.Text(" measured from outside the container.")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th()
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("without the cache")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("with the cache")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("docker start")
                    }
                    org.jetbrains.compose.web.dom.Text(" → ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("/health")
                    }
                    org.jetbrains.compose.web.dom.Text(", median of 10")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("4 380 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("2 042 ms")
                    }
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("the same, range")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("4 186–5 877 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("1 915–2 227 ms, one restart at 4 128")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("first signed-in screen, median")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("510 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("240 ms")
                    }
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("p50 / p95 of the next hundred requests")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("9.3 / 89 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("8.8 / 97 ms")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("the cache as an image layer")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("—")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("65 MiB")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Readiness halves, the first request halves, the hundred after it do not move. In the cluster the pod is Ready 3 s after its container starts, with the cache and a startup probe that asks every second; the 11 s it replaced included a readiness probe that waited five seconds before its first ask, so only part of that gap is the cache, and the gain reached a rollout at all only because the probe was retuned with it. The image totals ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("docker image ls")
        }
        org.jetbrains.compose.web.dom.Text(" printed for the two images, 602 and 685 MB, differ by more than the layer ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("docker history")
        }
        org.jetbrains.compose.web.dom.Text(" attributes to the cache; the layer is what the cache adds. The methodology and the CSVs are in the repository under ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("docs/research/measurements-2026-09-07/aot/")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The share of classes matters more than the milliseconds, because it is what the milliseconds follow from. On the Ktor stand in ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/zavarnik") {
            org.jetbrains.compose.web.dom.Text("zavarnik")
        }
        org.jetbrains.compose.web.dom.Text(" — Ktor 3.5.2 on CIO, kotlinx.serialization, three routes, 26 jars — 2 322 of 2 322 application, Ktor, coroutines and stdlib classes came from the cache (")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-Xlog:class+load")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("source: shared objects file")
        }
        org.jetbrains.compose.web.dom.Text("), and time to the first ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("200")
        }
        org.jetbrains.compose.web.dom.Text(" on ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/health")
        }
        org.jetbrains.compose.web.dom.Text(" fell by 59–69 % over four series of twenty runs on two collectors. A service whose start is not class loading gains less: a broker of mine on the same JDK build went from 1 377 to 1 225 ms at the median of seven runs, eleven per cent, because most of its start is opening its log.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Memory is the question I get asked next, so I measured it: RSS of the sample from ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/proc/<pid>/status")
        }
        org.jetbrains.compose.web.dom.Text(", five starts per variant in alternation, read at readiness and after 200 requests.")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th()
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("without the cache")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("with the cache")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("RSS at readiness, median of 5")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("85.0 MiB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("84.7 MiB")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("RSS after 200 requests")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("122.9 MiB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("119.6 MiB")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The sample's 31 MiB cache is not visible in resident memory; the difference is smaller than the spread of either row. What it costs is disk — the file and its layer in every image — and the training run: after the training JVM exits a second one assembles the cache, and JEP 514 says that sub-invocation \"uses its own Java heap with the same size as the heap used for the training run\" — a 4 GB training heap needs 8 GB.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("why-spring-and-quarkus-have-it-and-you-do-not") }) {
        org.jetbrains.compose.web.dom.Text("Why Spring and Quarkus have it and you do not")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Spring Boot's recipe is one property: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-Dspring.context.exit=onRefresh")
        }
        org.jetbrains.compose.web.dom.Text(" makes the application exit as soon as the context is up, so the training run ends by itself. Its documentation lists the conditions in one paragraph: the classpath \"must be the same as the one used to create it, in the same order\", extra jars only \"at the end (but will not be cached)\", \"the timestamps of the JARs must be preserved\", and \"avoid the usage of directories and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("*")
        }
        org.jetbrains.compose.web.dom.Text(" wildcard characters\". Quarkus runs its ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("@QuarkusIntegrationTest")
        }
        org.jetbrains.compose.web.dom.Text(" tests as the training under ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("./mvnw verify -Dquarkus.package.jar.aot.enabled=true -DskipITs=false")
        }
        org.jetbrains.compose.web.dom.Text(" and packs the result. Both frameworks own three things a plain ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("application")
        }
        org.jetbrains.compose.web.dom.Text(" distribution does not:")
    }
    org.jetbrains.compose.web.dom.Ol {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Training.")
            }
            org.jetbrains.compose.web.dom.Text(" Something has to start the application, drive it through the hot path — the cache holds what the run loaded, so an idle start yields a cache that covers half the classes — and stop it in a way that writes the cache. ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("SIGTERM")
            }
            org.jetbrains.compose.web.dom.Text(" and ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("System.exit")
            }
            org.jetbrains.compose.web.dom.Text(" write it; ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("SIGKILL")
            }
            org.jetbrains.compose.web.dom.Text(" does not. Then wait: the process runs a second JVM to assemble the file, and the application process being gone does not mean the cache is there.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Verification.")
            }
            org.jetbrains.compose.web.dom.Text(" A rejected cache is silent by default. The only way to know that production will use the cache is to start with it made mandatory (")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("-XX:AOTMode=on")
            }
            org.jetbrains.compose.web.dom.Text(") and count classes. And on JDK 25.0.0–25.0.3 and 26.0.0–26.0.1 even that is not enough: those builds never compare the cache with the jars, so a jar rebuilt after training runs its old classes with exit code 0 — ")
            com.varabyte.kobweb.silk.components.navigation.Link("/blog/stale-aot-cache-on-jdk25") {
                org.jetbrains.compose.web.dom.Text("the previous post")
            }
            org.jetbrains.compose.web.dom.Text(" has the logs. Something other than the JVM has to compare the jars.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Packaging.")
            }
            org.jetbrains.compose.web.dom.Text(" The JVM checks each jar's size and modification time, the classpath string, and the JDK down to the size of ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("lib/modules")
            }
            org.jetbrains.compose.web.dom.Text(". A zip loses the timestamps. A ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("-jre")
            }
            org.jetbrains.compose.web.dom.Text(" image refuses a cache trained on the ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("-jdk")
            }
            org.jetbrains.compose.web.dom.Text(" image of the same build. A ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("lib/*")
            }
            org.jetbrains.compose.web.dom.Text(" wildcard expands in the filesystem's own order, which is not the same order on every container runtime.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-same-by-hand-in-five-steps") }) {
        org.jetbrains.compose.web.dom.Text("The same by hand, in five steps")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("For a service on the ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("application")
        }
        org.jetbrains.compose.web.dom.Text(" plugin with a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/health")
        }
        org.jetbrains.compose.web.dom.Text(" route on 8080. Every step is what the plugin does; nothing here needs it.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("1. Install, and pin the jar timestamps.")
        }
        org.jetbrains.compose.web.dom.Text(" The JVM compares mtimes in whole seconds against the cache, and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("distTar")
        }
        org.jetbrains.compose.web.dom.Text(" and Docker ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("COPY")
        }
        org.jetbrains.compose.web.dom.Text(" preserve them; set them to a constant now so the cache matches wherever the tar is unpacked. The constant is Gradle's own for reproducible tars — ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("TarCopyAction.CONSTANT_TIME_FOR_TAR_ENTRIES")
        }
        org.jetbrains.compose.web.dom.Text(", 2 January 1970: one day rather than zero, because some tools read a zero mtime as no timestamp at all.")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-bash") }) { org.jetbrains.compose.web.dom.Text("""./gradlew installDist && cd build/install/app
find lib -name '*.jar' -exec touch -d @86400 {} +
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("2. Train.")
        }
        org.jetbrains.compose.web.dom.Text(" Start with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-XX:AOTCacheOutput")
        }
        org.jetbrains.compose.web.dom.Text(", wait for readiness, hit the hot path, stop with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("SIGTERM")
        }
        org.jetbrains.compose.web.dom.Text(", and wait for the process — after the application exits it runs a second JVM that assembles the cache and prints ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("AOTCache creation is complete")
        }
        org.jetbrains.compose.web.dom.Text(" when it is done. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-XX:-AOTAdapterCaching")
        }
        org.jetbrains.compose.web.dom.Text(" keeps machine code for the training CPU's instruction set out of the file; it is a diagnostic flag, which is what ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-XX:+UnlockDiagnosticVMOptions")
        }
        org.jetbrains.compose.web.dom.Text(" is for, and a later JDK may rename or drop it — the mainline loader already checks CPU features itself. A cache trained on a CI runner with AVX-512 has been reported to ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("SIGILL")
        }
        org.jetbrains.compose.web.dom.Text(" on a narrower machine, and a cache trained without that code is accepted by a JVM started without the flags.")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-bash") }) { org.jetbrains.compose.web.dom.Text("""JAVA_OPTS="-XX:AOTCacheOutput=${'$'}PWD/lib/app.aot -XX:+UnlockDiagnosticVMOptions -XX:-AOTAdapterCaching" bin/app &
until curl -sf localhost:8080/health >/dev/null; do sleep 0.1; done
for i in ${'$'}(seq 20); do curl -s localhost:8080/api/items >/dev/null; done
kill -TERM ${'$'}! && wait ${'$'}!
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("3. Write down what the cache was trained against.")
        }
        org.jetbrains.compose.web.dom.Text(" On a fixed JDK the JVM will refuse a changed jar; on the six builds that skip the check this file is the only thing that will.")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-bash") }) { org.jetbrains.compose.web.dom.Text("""sha256sum lib/*.jar > lib/app.aot.jars
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("4. Verify.")
        }
        org.jetbrains.compose.web.dom.Text(" Start with the cache mandatory and class loading logged, then count. The application classes must all come from ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("shared objects file")
        }
        org.jetbrains.compose.web.dom.Text("; a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("source: file:")
        }
        org.jetbrains.compose.web.dom.Text(" line for one of them names what the training did not reach.")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-bash") }) { org.jetbrains.compose.web.dom.Text("""sha256sum -c --quiet lib/app.aot.jars
JAVA_OPTS="-XX:AOTCache=${'$'}PWD/lib/app.aot -XX:AOTMode=on -Xlog:class+load=info" bin/app > verify.log &
until curl -sf localhost:8080/health >/dev/null; do sleep 0.1; done; kill -TERM ${'$'}!
grep -c 'source: shared objects file' verify.log; grep 'source: file:' verify.log | head
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("5. Ship.")
        }
        org.jetbrains.compose.web.dom.Text(" The start script has to add ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-XX:AOTCache")
        }
        org.jetbrains.compose.web.dom.Text(" only when the file is there, because the JVM refuses that flag next to ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-XX:AOTCacheOutput")
        }
        org.jetbrains.compose.web.dom.Text(" and the same script is the training launcher. Three lines before the ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("exec")
        }
        org.jetbrains.compose.web.dom.Text(" in ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("bin/app")
        }
        org.jetbrains.compose.web.dom.Text(":")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-bash") }) { org.jetbrains.compose.web.dom.Text("""if [ -f "${'$'}APP_HOME/lib/app.aot" ]; then
    DEFAULT_JVM_OPTS="${'$'}DEFAULT_JVM_OPTS \"-XX:AOTCache=${'$'}APP_HOME/lib/app.aot\""
fi
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Then ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("distTar")
        }
        org.jetbrains.compose.web.dom.Text(", not ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("distZip")
        }
        org.jetbrains.compose.web.dom.Text(", and in a Dockerfile steps 2–4 run in the runtime stage, on the ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-jre")
        }
        org.jetbrains.compose.web.dom.Text(" image that will start the service — not in the ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-jdk")
        }
        org.jetbrains.compose.web.dom.Text(" build stage, whose cache the runtime JVM will refuse. Steps 1–4 again after every change to any jar, on every build.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-same-through-the-plugin") }) {
        org.jetbrains.compose.web.dom.Text("The same through the plugin")
    }
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/zavarnik") {
            org.jetbrains.compose.web.dom.Text("zavarnik")
        }
        org.jetbrains.compose.web.dom.Text(" is those five steps as Gradle tasks, with the verification made a red build. It is on the Plugin Portal, so this is the whole installation:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""plugins {
    application
    id("io.github.youndie.zavarnik") version "0.1.0"
}

zavarnik {
    training {
        readyWhen.url("http://127.0.0.1:8080/health")
        workload {
            post("http://127.0.0.1:8080/auth/login", "application/json", ${"\"\"\""}{"user":"demo"}${"\"\"\""}) {
                capture("token", "accessToken")
            }
            get("http://127.0.0.1:8080/api/home") { header("Authorization", "Bearer {{token}}") }
        }
    }
}
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("./gradlew check")
        }
        org.jetbrains.compose.web.dom.Text(" runs ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("aotTrain")
        }
        org.jetbrains.compose.web.dom.Text(" — the installed distribution through its own start script, timestamps pinned, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("SIGTERM")
        }
        org.jetbrains.compose.web.dom.Text(", the manifest — and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("aotVerify")
        }
        org.jetbrains.compose.web.dom.Text(", which fails the build with the name of the thing that failed: a jar that changed since training, a JVM that refused the cache (with the JVM's own reason), or a class share below 90 %. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("distTar")
        }
        org.jetbrains.compose.web.dom.Text(" carries the cache; ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("installDist")
        }
        org.jetbrains.compose.web.dom.Text(" refuses a wildcard on the classpath. For the image, every distribution's ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("lib/")
        }
        org.jetbrains.compose.web.dom.Text(" carries ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("zavarnik-runner.jar")
        }
        org.jetbrains.compose.web.dom.Text(", the same training and verification on a bare JRE with no Gradle and no curl, so the runtime stage of the Dockerfile trains the cache on the JVM that will use it:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-dockerfile") }) { org.jetbrains.compose.web.dom.Text("""FROM eclipse-temurin:25.0.4_7-jre
COPY --from=build /src/build/install/app /opt/app
RUN java -cp /opt/app/lib/zavarnik-runner.jar io.github.youndie.zavarnik.runner.Main train /opt/app \
 && java -cp /opt/app/lib/zavarnik-runner.jar io.github.youndie.zavarnik.runner.Main verify /opt/app
CMD ["/opt/app/bin/app"]
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A service that cannot start on the build machine sets ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("training { onAssemble = false }")
        }
        org.jetbrains.compose.web.dom.Text(" and trains where it can — konekt does it in its release pipeline, inside a container of the release image on the stand's network, and adds the cache as a layer. On ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ktor { docker { } }")
        }
        org.jetbrains.compose.web.dom.Text(" or Jib directly, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("jib { containerizingMode = \"packaged\" }")
        }
        org.jetbrains.compose.web.dom.Text(" plus ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("jibAotTrain")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("jibAotVerify")
        }
        org.jetbrains.compose.web.dom.Text(" do the same through a container of the Jib image; that path needs a Docker daemon on the build machine. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("aotReport")
        }
        org.jetbrains.compose.web.dom.Text(" prints readiness with and without the cache and, as a second table, what the JIT still compiles afterwards — so nobody reads the first table as a warm service.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-breaks") }) {
        org.jetbrains.compose.web.dom.Text("What breaks")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("A stale cache accepted on JDK 25.0.0–25.0.3 and 26.0.0–26.0.1.")
            }
            org.jetbrains.compose.web.dom.Text(" The JVM never looks at the jars, ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("-XX:AOTMode=on")
            }
            org.jetbrains.compose.web.dom.Text(" exits 0, and the old classes run. Move to 25.0.4 or 26.0.2; until then the manifest from step 3 is the check (")
            com.varabyte.kobweb.silk.components.navigation.Link("/blog/stale-aot-cache-on-jdk25") {
                org.jetbrains.compose.web.dom.Text("the post")
            }
            org.jetbrains.compose.web.dom.Text(").")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("-jdk")
                }
                org.jetbrains.compose.web.dom.Text(" at training, ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("-jre")
                }
                org.jetbrains.compose.web.dom.Text(" in production.")
            }
            org.jetbrains.compose.web.dom.Text(" The same Temurin build, two sizes of ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("lib/modules")
            }
            org.jetbrains.compose.web.dom.Text(", cache refused: ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("This file is not the one used while building the AOT cache: '/opt/java/openjdk/lib/modules', size has changed")
            }
            org.jetbrains.compose.web.dom.Text(". Train in the image that runs.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("A wildcard on the classpath.")
            }
            org.jetbrains.compose.web.dom.Text(" ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("lib/*")
            }
            org.jetbrains.compose.web.dom.Text(" expands in ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("readdir")
            }
            org.jetbrains.compose.web.dom.Text(" order; Docker's overlay2 on the CI runner and containerd on a k0s node answered differently, and the first cache to reach a cluster was refused there with ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("The name of app classpath [1] does not match")
            }
            org.jetbrains.compose.web.dom.Text(". No check on the build machine can see this, because it verifies where it trained. List the jars.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Anything that changes the JVM's view of the classpath or modules on one side only:")
            }
            org.jetbrains.compose.web.dom.Text(" an entry added at the front (at the end is fine), ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("--add-modules")
            }
            org.jetbrains.compose.web.dom.Text(", a ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("-javaagent")
            }
            org.jetbrains.compose.web.dom.Text(" (an agent on both sides is fine), ZGC on one side and another collector on the other, on JDK 25 and 26 alike — the boundary is compressed oops, not the collector; ZGC on both sides works.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("A killed training run.")
            }
            org.jetbrains.compose.web.dom.Text(" ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("SIGKILL")
            }
            org.jetbrains.compose.web.dom.Text(", or a container stopped without a grace period, writes no cache and no error.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("What the plugin's first version does not do: train on Windows (no ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("SIGTERM")
        }
        org.jetbrains.compose.web.dom.Text(" there; production on Windows works), ship a cache in ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("distZip")
        }
        org.jetbrains.compose.web.dom.Text(", make a missing cache fatal in production (the start script runs without it, by design), or reproduce the cross-CPU ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("SIGILL")
        }
        org.jetbrains.compose.web.dom.Text(" on its own hardware — the adapter code is left out by default on the strength of two published reports, and a positive control on a pair of machines is still open.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Source: ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/zavarnik") {
            org.jetbrains.compose.web.dom.Text("github.com/youndie/zavarnik")
        }
        org.jetbrains.compose.web.dom.Text(" — ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("experiments/ktor-readiness/")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("experiments/jit-warmup/")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("experiments/memory/")
        }
        org.jetbrains.compose.web.dom.Text(" for the stand figures with their logs, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("docs/research/research-architecture.md")
        }
        org.jetbrains.compose.web.dom.Text(" §1 for what the JVM checks, with a path for every row; ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/konekt") {
            org.jetbrains.compose.web.dom.Text("github.com/youndie/konekt")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("docs/research/measurements-2026-09-07/aot/")
        }
        org.jetbrains.compose.web.dom.Text(", for the service. The bug this post keeps pointing at has its own write-up: ")
        com.varabyte.kobweb.silk.components.navigation.Link("/blog/stale-aot-cache-on-jdk25") {
            org.jetbrains.compose.web.dom.Text("OpenJDK 25.0.0–25.0.3 uses a stale AOT cache without saying so")
        }
        org.jetbrains.compose.web.dom.Text(". The other way to start faster — a snapshot of the warmed-up process, JIT included — is ")
        com.varabyte.kobweb.silk.components.navigation.Link("/blog/crac-on-a-ktor-service-with-hikari-and-exposed") {
            org.jetbrains.compose.web.dom.Text("CRaC on a Ktor service with HikariCP and Exposed")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
}
